import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * The patient's OWN lab and vital trend series, for the v2 portal's Health page (PD-1602).
 *
 * Self-scoped like `/v2/patient/me/clinical-summary`: the input carries no patient id — the
 * handler derives the subject from the authenticated profile. The wire is raw series data
 * (points, unit, reference range, trend direction + delta), deliberately not the ChartSpec
 * artifacts the assistant/clinical summary render, so the portal stays decoupled from them.
 *
 * A lab or vital series whose readings come in two or more units (after the model's vital
 * conversions and unit spelling aliases) is sent as one series per unit (PD-1923), never
 * converted. Its `key` is '<analyte key>::<unit key>' ('::no-unit' for readings with no unit), and
 * its `name` stays the analyte's name: the portal adds the unit to the label. Its `unit`, `trend`,
 * `delta`, `hasAbnormal` (labs) and `points` cover only that unit's readings. A series in one unit
 * keeps its plain key.
 *
 * `page` mirrors ModelPageRequest and applies to each category's observation fetch (labs and
 * vitals separately), newest first. Each category echoes it back as a ModelPage, as listPatients
 * does. `size` and `total` count observation readings, not series. History is partial whenever
 * `offset > 0` (newer readings skipped), `incompleteTotal` (older readings may exist beyond the
 * page) or `truncated` (Medplum's search cap cut the page short), so the UI must not present it as
 * complete in any of those cases. For vitals `truncated` is also set when the lookup of panels
 * outside the page hit that cap: the page is complete, but some readings may still show their
 * import date instead of their clinical date.
 */

// Mirrors the model's TrendDirection / AbnormalFlag (src/model/observation/types.ts). Inlined
// because this contract is bundled into the frontends via @behuman/core-contract.
const trendSchema = z.enum(['rising', 'falling', 'stable', 'fluctuating', 'insufficient'])
const abnormalFlagSchema = z
  .enum(['high', 'low', 'critical-high', 'critical-low', 'abnormal'])
  .nullable()

/**
 * HL7 v3 ObservationInterpretation codes a lab may send as its whole call. The portal words each
 * one (patient copy lives in the frontend); `abnormalFlag` carries whether it is abnormal.
 */
export const LAB_INTERPRETATION_CODES = [
  'N',
  'POS',
  'NEG',
  'DET',
  'ND',
  'IND',
  'E',
  'RR',
  'WR',
  'NR',
  'R',
  'S',
  'I',
  'NS',
  'EXP',
  'UNE',
] as const

// The lab's own call, as data: its wording, or a known code the portal words.
export const labCallFields = {
  interpretation: z.string().optional(),
  interpretationCode: z.enum(LAB_INTERPRETATION_CODES).optional(),
}

export const labQuantitySchema = z.object({
  value: z.number(),
  unit: z.string().nullable(),
  comparator: z.string().nullable(),
})

export const labPointSchema = z.object({
  date: z.string().nullable(),
  value: z.number().nullable(),
  comparator: z.enum(['<', '<=', '>=', '>']).nullable(),
  rawValue: z.string().nullable(),
  unit: z.string().nullable(),
  referenceLow: z.number().nullable(),
  referenceHigh: z.number().nullable(),
  // The document's range as written (e.g. '<5.7 %', 'Negative'); some give only this, no bounds.
  referenceText: z.string().nullable(),
  // A range in a unit other than the reading's, with no text: its bounds as sent, for the portal
  // to word. They never flag the reading and are not in referenceLow/referenceHigh.
  otherUnitRange: z
    .object({ low: labQuantitySchema.nullable(), high: labQuantitySchema.nullable() })
    .optional(),
  // The lab called this reading Normal (the code N, or its Normal wording). Its call stands over
  // the range, as in the lab report, so the portal does not judge the reading by its bounds.
  labCall: z.literal('normal').optional(),
  abnormalFlag: abnormalFlagSchema,
  // The lab's own call on the reading, read as the lab report's table row reads it (patient views),
  // so the portal never calls Normal a result the lab called Indeterminate or Equivocal.
  ...labCallFields,
})

const labSeriesSchema = z.object({
  // The analyte key, or '<analyte key>::<unit key>' for one unit of a series split by unit.
  key: z.string(),
  // The analyte's name, also on each unit of a split series.
  name: z.string().nullable(),
  loincCode: z.string().nullable(),
  // The latest exact numeric reading's unit, else the latest qualified one (a trailing text result
  // does not blank it). Every numeric point is in this unit, since a series in several units is split.
  unit: z.string().nullable(),
  valueType: z.enum(['numeric', 'text']),
  trend: trendSchema,
  // last − first exact numeric value, in `unit`; qualified readings remain in points but do not
  // establish a change. Null when fewer than two exact points (and `trend` is then 'insufficient').
  delta: z.number().nullable(),
  // Whether any of this series' points (only this unit's, for a split series) carries a flag.
  hasAbnormal: z.boolean(),
  // The name of the lab panel (e.g. 'CBC & Differential') of the latest reading that has one, as
  // the Clinical Summary groups it (PD-2039); null when no reading links to a panel.
  panel: z.string().nullable().optional(),
  points: z.array(labPointSchema),
})

const vitalPointSchema = z.object({
  date: z.string().nullable(),
  value: z.number().nullable(),
  unit: z.string().nullable(),
  // This reading's own range from its document, in `unit`; ranges differ between labs, so each
  // reading keeps its own. Null when the document gives none (or gives it in another unit).
  referenceLow: z.number().nullable(),
  referenceHigh: z.number().nullable(),
  // Inclusive against the document range, a missing bound open, decided on the unrounded value
  // and bounds: a converted reading whose displayed value equals a displayed bound can still be
  // false, so use this flag rather than recomputing it. Null when there is no range.
  inRange: z.boolean().nullable(),
})

const vitalSeriesSchema = z.object({
  // As for labs: the vital key, or '<vital key>::<unit key>' for one unit of a split series.
  key: z.string(),
  name: z.string().nullable(),
  loincCode: z.string().nullable(),
  // The latest reading's unit after the model's conversions (kg→lbs, °C→°F, …); a series still in
  // several units is split, so every point is in this unit.
  unit: z.string().nullable(),
  trend: trendSchema,
  // last − first value, in `unit`; null when fewer than two numeric points.
  delta: z.number().nullable(),
  points: z.array(vitalPointSchema),
})

// Bounded so a patient cannot ask for an unbounded fetch; 1000 matches the clinical summary's cap.
const pageRequestSchema = z.object({
  offset: z.number().int().min(0),
  count: z.number().int().min(1).max(1000),
  limit: z.number().int().min(1).max(1000),
})

// Mirrors ModelPage, the same envelope listPatients returns.
const pageSchema = pageRequestSchema.extend({
  size: z.number().int().min(0),
  total: z.number().int().min(0),
  // Set when Medplum's search cap cut the page short, or, for vitals, cut short the lookup of
  // panels outside the page, so some readings may still carry their import date.
  truncated: z.boolean().optional(),
  // Set when the page was filled, so `total` is a lower bound and older readings may exist.
  incompleteTotal: z.boolean().optional(),
})

export const patientMeMetricTrendsSchema = z.object({
  labs: z.object({ data: z.array(labSeriesSchema), page: pageSchema }),
  vitals: z.object({ data: z.array(vitalSeriesSchema), page: pageSchema }),
})

export const patientMeMetricTrends = oc
  .route({ method: 'POST', path: '/v2/patient/me/metric-trends' })
  .input(z.object({ page: pageRequestSchema }))
  .output(patientMeMetricTrendsSchema)
